import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api/v2';

const styles = {
  overlay: { position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.6)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' },
  modal: { background:'#fff', borderRadius:'12px', maxWidth:'700px', width:'100%', maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' },
  header: { padding:'1.5rem 2rem 1rem', borderBottom:'1px solid #e2e8f0' },
  title: { fontSize:'1.3rem', fontWeight:700, color:'#1e293b', margin:0 },
  subtitle: { fontSize:'0.85rem', color:'#64748b', marginTop:'0.25rem' },
  body: { padding:'1.5rem 2rem', overflowY:'auto', flex:1 },
  duaText: { background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:'8px', padding:'1.25rem', fontSize:'0.8rem', lineHeight:'1.6', color:'#334155', maxHeight:'300px', overflowY:'auto', whiteSpace:'pre-wrap', fontFamily:'inherit' },
  form: { marginTop:'1.25rem', display:'flex', flexDirection:'column', gap:'0.75rem' },
  label: { fontSize:'0.8rem', fontWeight:600, color:'#475569' },
  input: { padding:'0.6rem 0.75rem', border:'1px solid #d1d5db', borderRadius:'6px', fontSize:'0.9rem', outline:'none', width:'100%', boxSizing:'border-box' },
  checkRow: { display:'flex', alignItems:'flex-start', gap:'0.5rem', marginTop:'0.5rem' },
  checkbox: { marginTop:'3px', width:'18px', height:'18px', cursor:'pointer' },
  checkLabel: { fontSize:'0.82rem', color:'#334155', lineHeight:'1.4' },
  footer: { padding:'1rem 2rem 1.5rem', borderTop:'1px solid #e2e8f0', display:'flex', justifyContent:'flex-end', gap:'0.75rem' },
  btnSign: { padding:'0.6rem 1.5rem', background:'#2563eb', color:'#fff', border:'none', borderRadius:'6px', fontWeight:600, fontSize:'0.9rem', cursor:'pointer' },
  btnSignDisabled: { padding:'0.6rem 1.5rem', background:'#94a3b8', color:'#fff', border:'none', borderRadius:'6px', fontWeight:600, fontSize:'0.9rem', cursor:'not-allowed' },
  btnCancel: { padding:'0.6rem 1.5rem', background:'#f1f5f9', color:'#475569', border:'1px solid #d1d5db', borderRadius:'6px', fontWeight:600, fontSize:'0.9rem', cursor:'pointer' },
  shield: { fontSize:'2rem', marginBottom:'0.25rem' },
  error: { color:'#dc2626', fontSize:'0.8rem', marginTop:'0.25rem' },
};

export default function DUAModal({ onSigned, onCancel }) {
  const [duaText, setDuaText] = useState('');
  const [version, setVersion] = useState('');
  const [fullName, setFullName] = useState('');
  const [institution, setInstitution] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(API + '/dua/status').then(r => {
      if (r.data.dua_text) { setDuaText(r.data.dua_text); setVersion(r.data.current_version); }
    }).catch(() => {
      axios.get(API + '/dua/text').then(r => { setDuaText(r.data.text); setVersion(r.data.version); });
    });
  }, []);

  const handleScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) setScrolledToEnd(true);
  };

  const canSign = fullName.trim().length >= 3 && accepted && scrolledToEnd;

  const handleSign = async () => {
    if (!canSign) return;
    setSigning(true); setError('');
    try {
      await axios.post(API + '/dua/sign', { full_name: fullName.trim(), institution: institution.trim() });
      onSigned();
    } catch(e) {
      setError(e.response?.data?.error || 'Signature failed. Please try again.');
    }
    setSigning(false);
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.shield}>🛡️</div>
          <h2 style={styles.title}>Data Use Agreement</h2>
          <p style={styles.subtitle}>Version {version} — You must accept before accessing or contributing patient data</p>
        </div>
        <div style={styles.body}>
          <div style={styles.duaText} onScroll={handleScroll}>
            {duaText || 'Loading agreement...'}
          </div>
          {!scrolledToEnd && <p style={{fontSize:'0.75rem',color:'#d97706',marginTop:'0.5rem'}}>⚠️ Please scroll to the end of the agreement to continue</p>}
          <div style={styles.form}>
            <div>
              <label style={styles.label}>Full Legal Name *</label>
              <input style={styles.input} placeholder="Dr. Jean Dupont" value={fullName} onChange={e => setFullName(e.target.value)} />
            </div>
            <div>
              <label style={styles.label}>Institution / Hospital</label>
              <input style={styles.input} placeholder="Hôpital Privé du dos Francheville" value={institution} onChange={e => setInstitution(e.target.value)} />
            </div>
            <div style={styles.checkRow}>
              <input type="checkbox" style={styles.checkbox} checked={accepted} onChange={e => setAccepted(e.target.checked)} id="dua-accept" />
              <label htmlFor="dua-accept" style={styles.checkLabel}>
                I have read and agree to all terms of the Data Use Agreement. I understand that violation of these terms may result in immediate revocation of access and potential legal action.
              </label>
            </div>
            {error && <p style={styles.error}>{error}</p>}
          </div>
        </div>
        <div style={styles.footer}>
          {onCancel && <button style={styles.btnCancel} onClick={onCancel}>Cancel</button>}
          <button style={canSign ? styles.btnSign : styles.btnSignDisabled} onClick={handleSign} disabled={!canSign || signing}>
            {signing ? 'Signing...' : '✍️ Sign Agreement'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Hook for pages to check DUA status
export function useDUACheck() {
  const [needsDUA, setNeedsDUA] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    axios.get(API + '/dua/status').then(r => {
      setNeedsDUA(r.data.needs_signature);
      setChecked(true);
    }).catch(() => setChecked(true));
  }, []);

  const refresh = () => {
    axios.get(API + '/dua/status').then(r => setNeedsDUA(r.data.needs_signature));
  };

  return { needsDUA, checked, refresh };
}
